import { useState, useEffect } from 'react';
import { track } from '../services/analyticsTracker';
import { ChevronLeft, ChevronRight, Calendar as CalendarIcon, Clock, MapPin, Plus, X, CheckCircle, Filter, MoreVertical, Edit2, Loader2 } from 'lucide-react';
import { calendarAPI } from '../services/api';
import { useAuth } from '../contexts/AuthContext';
import { BackHeader } from '../components/BackHeader';

interface CalendarEvent {
  id: string;
  title: string;
  description: string;
  startDate: string;
  endDate?: string;
  type: 'maintenance' | 'event' | 'meeting' | 'amenity' | 'general' | 'payment';
  createdBy: string;
  status: 'scheduled' | 'completed' | 'cancelled';
  location?: string;
  rsvpSummary?: { total: number; attending: number; not_attending: number; maybe: number };
  myRsvp?: 'attending' | 'not_attending' | 'maybe';
}

type RsvpStatus = 'attending' | 'not_attending' | 'maybe';
interface RsvpSummary { total: number; attending: number; not_attending: number; maybe: number; pending: number; }

const eventTypes = {
  maintenance: { label: 'Mantenimiento', color: 'bg-amber-500', text: 'text-amber-600', bg: 'bg-amber-50', border: 'border-amber-200' },
  event: { label: 'Evento', color: 'bg-purple-500', text: 'text-purple-600', bg: 'bg-purple-50', border: 'border-purple-200' },
  meeting: { label: 'Reunión', color: 'bg-blue-500', text: 'text-blue-600', bg: 'bg-blue-50', border: 'border-blue-200' },
  amenity: { label: 'Amenidad', color: 'bg-emerald-500', text: 'text-emerald-600', bg: 'bg-emerald-50', border: 'border-emerald-200' },
  general: { label: 'General', color: 'bg-[var(--v-text-secondary)]', text: 'text-[var(--v-text-secondary)]', bg: 'bg-[var(--v-surface-2)]', border: 'border-[var(--v-border)]' },
  payment: { label: 'Pago', color: 'bg-rose-500', text: 'text-rose-600', bg: 'bg-rose-50', border: 'border-rose-200' },
};

export default function Calendar() {
  const [currentDate, setCurrentDate] = useState(new Date());
  const [events, setEvents] = useState<CalendarEvent[]>([]);
  const [showModal, setShowModal] = useState(false);
  const [selectedDate, setSelectedDate] = useState<Date | null>(null);
  const [selectedEvent, setSelectedEvent] = useState<CalendarEvent | null>(null);
  const [rsvpSummary, setRsvpSummary] = useState<RsvpSummary | null>(null);
  const [myRsvp, setMyRsvp] = useState<RsvpStatus | null>(null);
  const [rsvpError, setRsvpError] = useState('');
  const [savingRsvp, setSavingRsvp] = useState(false);
  const [filterType, setFilterType] = useState<string>('all');
  const [newEvent, setNewEvent] = useState({
    title: '',
    description: '',
    startDate: '',
    endDate: '',
    type: 'general' as CalendarEvent['type'],
    location: ''
  });
  // F51: Edit event state
  const [showEditModal, setShowEditModal] = useState(false);
  const [editEvent, setEditEvent] = useState({
    id: '',
    title: '',
    description: '',
    date: '',
    endDate: '',
    type: 'general' as string,
    location: '',
  });
  const [savingEdit, setSavingEdit] = useState(false);
  const [editError, setEditError] = useState('');

  useEffect(() => {
    loadEvents();
  }, [currentDate]);

  // Normalize a raw event coming from the API (snake_case / partial) into the
  // CalendarEvent shape the UI expects. Guarantees `startDate` and `type` are
  // always valid so render-time code (filter/startsWith, eventTypes[type])
  // never crashes on undefined fields.
  const normalizeEvent = (raw: any): CalendarEvent => {
    const type: CalendarEvent['type'] =
      raw?.type && raw.type in eventTypes ? raw.type : 'general';
    return {
      id: String(raw?.id ?? ''),
      title: raw?.title ?? '',
      description: raw?.description ?? '',
      startDate: raw?.startDate ?? raw?.event_date ?? '',
      endDate: raw?.endDate ?? raw?.end_date ?? undefined,
      type,
      createdBy: raw?.createdBy ?? raw?.created_by ?? '',
      status: raw?.status ?? 'scheduled',
      location: raw?.location ?? undefined,
    };
  };

  const loadEvents = async () => {
    try {
      const user = JSON.parse(localStorage.getItem('user') || '{}');
      const condoId = user.condoId;
      const response = await calendarAPI.getEvents(condoId);
      // Backend responds with { success, data: rows }. Axios wraps that in
      // response.data, so the actual array lives at response.data.data.
      const payload = response?.data ?? response;
      const rows = Array.isArray(payload)
        ? payload
        : Array.isArray(payload?.data)
          ? payload.data
          : [];
      setEvents(rows.map(normalizeEvent));
    } catch (err) {
      console.error('Error loading events:', err);
      setEvents([]);
    }
  };

  const handleSelectEvent = async (event: CalendarEvent) => {
    setSelectedEvent(event);
    setRsvpSummary(null);
    setMyRsvp(null);
    setRsvpError('');
    try {
      const user = JSON.parse(localStorage.getItem('user') || '{}');
      const response = await calendarAPI.getRsvps(user.condoId, event.id);
      const payload = response?.data?.data ?? response?.data ?? response;
      const summary = payload?.summary;
      if (summary) setRsvpSummary(summary);
      const userId = user.id || user.userId;
      const mine = (payload?.rsvps || []).find((r: any) => String(r.user_id) === String(userId));
      if (mine?.status && ['attending', 'not_attending', 'maybe'].includes(mine.status)) setMyRsvp(mine.status);
    } catch (err) {
      console.error('Error loading RSVPs:', err);
    }
  };

  const submitRsvp = async (status: RsvpStatus) => {
    if (!selectedEvent || savingRsvp) return;
    setSavingRsvp(true);
    setRsvpError('');
    try {
      const user = JSON.parse(localStorage.getItem('user') || '{}');
      await calendarAPI.rsvp(user.condoId, selectedEvent.id, status);
      setMyRsvp(status);
      const response = await calendarAPI.getRsvps(user.condoId, selectedEvent.id);
      const payload = response?.data?.data ?? response?.data ?? response;
      const summary = payload?.summary as RsvpSummary | undefined;
      if (summary) {
        setRsvpSummary(summary);
        setSelectedEvent((event) => event ? { ...event, rsvpSummary: summary, myRsvp: status } : event);
        setEvents((currentEvents) => currentEvents.map((event) =>
          event.id === selectedEvent.id ? { ...event, rsvpSummary: summary, myRsvp: status } : event
        ));
      }
    } catch (err) {
      console.error('Error submitting RSVP:', err);
      setRsvpError('No se pudo registrar tu respuesta');
    } finally {
      setSavingRsvp(false);
    }
  };

  const getDaysInMonth = (date: Date) => {
    const year = date.getFullYear();
    const month = date.getMonth();
    const firstDay = new Date(year, month, 1);
    const lastDay = new Date(year, month + 1, 0);
    const daysInMonth = lastDay.getDate();
    const startingDay = firstDay.getDay();

    const days: (number | null)[] = [];
    for (let i = 0; i < startingDay; i++) {
      days.push(null);
    }
    for (let i = 1; i <= daysInMonth; i++) {
      days.push(i);
    }
    return days;
  };

  const getEventsForDay = (day: number) => {
    const dateStr = `${currentDate.getFullYear()}-${String(currentDate.getMonth() + 1).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
    let dayEvents = events.filter(e => typeof e.startDate === 'string' && e.startDate.startsWith(dateStr));
    if (filterType !== 'all') {
      dayEvents = dayEvents.filter(e => e.type === filterType);
    }
    return dayEvents;
  };

  const handleDayClick = (day: number) => {
    const clickedDate = new Date(currentDate.getFullYear(), currentDate.getMonth(), day);
    setSelectedDate(clickedDate);
    setNewEvent({
      ...newEvent,
      startDate: clickedDate.toISOString().slice(0, 16)
    });
    setShowModal(true);
  };

  const handleCreateEvent = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      const user = JSON.parse(localStorage.getItem('user') || '{}');
      const condoId = user.condoId;
      // Backend expects `eventDate`/`endDate` (it ignores `startDate`). Sending
      // the wrong key left eventDate undefined -> 400 "Faltan campos requeridos".
      const eventData = {
        title: newEvent.title,
        description: newEvent.description,
        eventDate: newEvent.startDate,
        endDate: newEvent.endDate || undefined,
        type: newEvent.type,
        location: newEvent.location,
        createdBy: user.name || 'Usuario',
      };
      const response = await calendarAPI.createEvent(condoId, eventData);
      track('calendar_event_created');
      // POST only returns { success, data: { id } }. Build a complete, valid
      // CalendarEvent from local form data + the returned id so appending it to
      // state never yields an event with undefined startDate/type (which would
      // crash render via startsWith / eventTypes[type]).
      const payload = response?.data ?? response;
      const newId = payload?.data?.id ?? payload?.id ?? Date.now().toString();
      const createdEvent = normalizeEvent({
        id: newId,
        title: newEvent.title,
        description: newEvent.description,
        event_date: newEvent.startDate,
        end_date: newEvent.endDate || undefined,
        type: newEvent.type,
        location: newEvent.location,
        created_by: user.name || 'Usuario',
        status: 'scheduled',
      });
      setEvents((prev) => [...prev, createdEvent]);
      setShowModal(false);
      setNewEvent({ title: '', description: '', startDate: '', endDate: '', type: 'general', location: '' });
    } catch (err) {
      console.error('Error creating event:', err);
      alert('Error al crear el evento');
    }
  };

  const deleteEvent = async (id: string) => {
    if (confirm('¿Eliminar este evento?')) {
      try {
        await calendarAPI.deleteEvent(id);
        setEvents(events.filter(e => e.id !== id));
        setSelectedEvent(null);
      } catch (err) {
        console.error('Error deleting event:', err);
        alert('Error al eliminar el evento');
      }
    }
  };

  // F51: Open edit modal pre-filled with current event values
  const openEditModal = (event: CalendarEvent) => {
    const dateStr = event.startDate ? event.startDate.slice(0, 16) : '';
    const eventType: string = event.type in eventTypes ? event.type : 'general';
    const endDateStr = event.endDate ? event.endDate.slice(0, 16) : '';
    setEditEvent({
      id: event.id,
      title: event.title,
      description: event.description,
      date: dateStr,
      endDate: endDateStr,
      type: eventType,
      location: event.location || '',
    });
    setEditError('');
    setShowEditModal(true);
  };

  // F51: Handle edit event submission
  const handleEditEvent = async (e: React.FormEvent) => {
    e.preventDefault();
    setEditError('');
    setSavingEdit(true);
    try {
      const user = JSON.parse(localStorage.getItem('user') || '{}');
      const condoId = user.condoId;
      const payload: any = {
        title: editEvent.title,
        description: editEvent.description,
        date: editEvent.date,
        type: editEvent.type,
      };
      if (editEvent.endDate) payload.endDate = editEvent.endDate;
      if (editEvent.location) payload.location = editEvent.location;
      await calendarAPI.editEvent(condoId, editEvent.id, payload);
      setEvents(prev => prev.map(ev =>
        ev.id === editEvent.id
          ? { ...ev, title: editEvent.title, description: editEvent.description, startDate: editEvent.date, endDate: editEvent.endDate || undefined, type: editEvent.type as CalendarEvent['type'], location: editEvent.location || undefined }
          : ev
      ));
      if (selectedEvent && selectedEvent.id === editEvent.id) {
        setSelectedEvent({ ...selectedEvent, title: editEvent.title, description: editEvent.description, startDate: editEvent.date, endDate: editEvent.endDate || undefined, type: editEvent.type as CalendarEvent['type'], location: editEvent.location || undefined });
      }
      setShowEditModal(false);
    } catch (err: any) {
      const msg = err?.response?.data?.error || 'Error al editar el evento';
      setEditError(typeof msg === 'string' ? msg : 'Error al editar el evento');
    } finally {
      setSavingEdit(false);
    }
  };

  const monthNames = [
    'Enero', 'Febrero', 'Marzo', 'Abril', 'Mayo', 'Junio',
    'Julio', 'Agosto', 'Septiembre', 'Octubre', 'Noviembre', 'Diciembre'
  ];

  const weekDays = ['Dom', 'Lun', 'Mar', 'Mié', 'Jue', 'Vie', 'Sáb'];

  const upcomingEvents = events
    .filter(e => new Date(e.startDate) >= new Date())
    .sort((a, b) => new Date(a.startDate).getTime() - new Date(b.startDate).getTime())
    .slice(0, 5);

  // F51: Determine if current user is admin or directivo (treasurer) for edit visibility
  const isAdminRole = (() => {
    try {
      const u = JSON.parse(localStorage.getItem('user') || '{}');
      const role = (u.role || u.condoRole || '').toLowerCase();
      const roles = Array.isArray(u.roles) ? u.roles : [];
      return role === 'admin' || role === 'super_admin' || role === 'treasurer' ||
             roles.some((r: any) => {
               const rl = (typeof r === 'string' ? r : r?.role || '').toLowerCase();
               return rl === 'admin' || rl === 'super_admin' || rl === 'treasurer';
             });
    } catch { return false; }
  })();

  return (
    <div className="space-y-6">
      <BackHeader fallback='/building/dashboard' />
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h2 className="text-2xl font-bold text-[var(--v-text-primary)]">Calendario</h2>
          <p className="text-[var(--v-text-secondary)] mt-1">Gestiona eventos y reservas de tu comunidad</p>
        </div>
        <div className="flex items-center gap-3">
          <select
            value={filterType}
            onChange={(e) => setFilterType(e.target.value)}
            className="px-4 py-2.5 bg-[var(--v-surface)] border border-[var(--v-border)] rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
          >
            <option value="all">Todos los tipos</option>
            {Object.entries(eventTypes).map(([key, type]) => (
              <option key={key} value={key}>{type.label}</option>
            ))}
          </select>
          <button 
            onClick={() => setShowModal(true)}
            className="inline-flex items-center gap-2 px-4 py-2.5 bg-blue-600 hover:bg-blue-700 text-white rounded-lg font-medium transition-all hover:shadow-lg hover:shadow-blue-600/25"
          >
            <Plus className="w-5 h-5" />
            Nuevo Evento
          </button>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Calendar */}
        <div className="lg:col-span-2 bg-[var(--v-surface)] rounded-xl border border-[var(--v-border)] shadow-sm overflow-hidden">
          {/* Calendar Header */}
          <div className="p-4 border-b border-[var(--v-border)] flex items-center justify-between">
            <h3 className="text-lg font-semibold text-[var(--v-text-primary)]">
              {monthNames[currentDate.getMonth()]} {currentDate.getFullYear()}
            </h3>
            <div className="flex items-center gap-2">
              <button
                onClick={() => setCurrentDate(new Date(currentDate.getFullYear(), currentDate.getMonth() - 1, 1))}
                className="p-2 hover:bg-[var(--v-surface-2)] rounded-lg transition-colors"
              >
                <ChevronLeft className="w-5 h-5 text-[var(--v-text-secondary)]" />
              </button>
              <button
                onClick={() => setCurrentDate(new Date())}
                className="px-3 py-1.5 text-sm font-medium text-[var(--v-text-secondary)] hover:bg-[var(--v-surface-2)] rounded-lg transition-colors"
              >
                Hoy
              </button>
              <button
                onClick={() => setCurrentDate(new Date(currentDate.getFullYear(), currentDate.getMonth() + 1, 1))}
                className="p-2 hover:bg-[var(--v-surface-2)] rounded-lg transition-colors"
              >
                <ChevronRight className="w-5 h-5 text-[var(--v-text-secondary)]" />
              </button>
            </div>
          </div>

          {/* Week Days */}
          <div className="grid grid-cols-7 border-b border-[var(--v-border)]">
            {weekDays.map((day) => (
              <div key={day} className="py-3 text-center text-xs font-semibold text-[var(--v-text-secondary)] uppercase tracking-wider">
                {day}
              </div>
            ))}
          </div>

          {/* Days Grid */}
          <div className="grid grid-cols-7">
            {getDaysInMonth(currentDate).map((day, index) => {
              const dayEvents = day ? getEventsForDay(day) : [];
              const isToday = day === new Date().getDate() && 
                currentDate.getMonth() === new Date().getMonth() &&
                currentDate.getFullYear() === new Date().getFullYear();

              return (
                <div
                  key={index}
                  onClick={() => day && handleDayClick(day)}
                  className={`min-h-[100px] p-2 border border-[var(--v-border)] ${
                    day ? 'cursor-pointer hover:bg-[var(--v-surface-2)]' : 'bg-slate-25'
                  } ${isToday ? 'bg-blue-50/50' : ''}`}
                >
                  {day && (
                    <>
                      <span className={`text-sm font-medium ${isToday ? 'text-blue-600 bg-blue-100 w-7 h-7 rounded-full flex items-center justify-center' : 'text-[var(--v-text-primary)]'}`}>
                        {day}
                      </span>
                      <div className="mt-1 space-y-1">
                        {dayEvents.slice(0, 2).map((event) => (
                          <div
                            key={event.id}
                            onClick={(e) => { e.stopPropagation(); handleSelectEvent(event); }}
                            className={`${eventTypes[event.type].color} text-white text-xs px-2 py-1 rounded truncate cursor-pointer hover:opacity-90`}
                          >
                            {event.title}
                          </div>
                        ))}
                        {dayEvents.length > 2 && (
                          <div className="text-[var(--v-text-tertiary)] text-xs pl-1">+{dayEvents.length - 2} más</div>
                        )}
                      </div>
                    </>
                  )}
                </div>
              );
            })}
          </div>
        </div>

        {/* Sidebar */}
        <div className="space-y-6">
          {/* Legend */}
          <div className="bg-[var(--v-surface)] p-5 rounded-xl border border-[var(--v-border)] shadow-sm">
            <h3 className="text-sm font-semibold text-[var(--v-text-primary)] mb-4">Tipos de Evento</h3>
            <div className="space-y-2">
              {Object.entries(eventTypes).map(([key, type]) => (
                <div key={key} className="flex items-center gap-3">
                  <div className={`w-3 h-3 rounded-full ${type.color}`}></div>
                  <span className="text-sm text-[var(--v-text-secondary)]">{type.label}</span>
                </div>
              ))}
            </div>
          </div>

          {/* Upcoming Events */}
          <div className="bg-[var(--v-surface)] p-5 rounded-xl border border-[var(--v-border)] shadow-sm">
            <h3 className="text-sm font-semibold text-[var(--v-text-primary)] mb-4">Próximos Eventos</h3>
            {upcomingEvents.length === 0 ? (
              <p className="text-[var(--v-text-secondary)] text-sm">No hay eventos programados</p>
            ) : (
              <div className="space-y-3">
                {upcomingEvents.map((event) => (
                  <div
                    key={event.id}
                    onClick={() => handleSelectEvent(event)}
                    className="flex items-start gap-3 p-3 bg-[var(--v-surface-2)] rounded-xl cursor-pointer hover:bg-[var(--v-surface-2)] transition-colors"
                  >
                    <div className={`w-2 h-2 rounded-full mt-2 ${eventTypes[event.type].color}`} />
                    <div className="flex-1 min-w-0">
                      <h4 className="text-sm font-medium text-[var(--v-text-primary)] truncate">{event.title}</h4>
                      <p className="text-xs text-[var(--v-text-secondary)] mt-0.5">
                        {new Date(event.startDate).toLocaleDateString('es-ES', { day: 'numeric', month: 'short' })}
                        {' • '}
                        {new Date(event.startDate).toLocaleTimeString('es-ES', { hour: '2-digit', minute: '2-digit' })}
                      </p>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Create Event Modal */}
      {showModal && (
        <div className="fixed inset-0 bg-slate-900/50 flex items-center justify-center z-50 p-4">
          <div className="bg-[var(--v-surface)] rounded-2xl shadow-xl max-w-md w-full max-h-[90vh] overflow-y-auto">
            <div className="p-6 border-b border-[var(--v-border)] flex items-center justify-between">
              <h3 className="text-lg font-semibold text-[var(--v-text-primary)]">
                {selectedDate ? `Nuevo Evento - ${selectedDate.toLocaleDateString()}` : 'Nuevo Evento'}
              </h3>
              <button 
                onClick={() => setShowModal(false)}
                className="p-2 text-[var(--v-text-tertiary)] hover:text-[var(--v-text-secondary)] hover:bg-[var(--v-surface-2)] rounded-lg transition-colors"
              >
                <X className="w-5 h-5" />
              </button>
            </div>
            <form onSubmit={handleCreateEvent} className="p-6 space-y-4">
              <div>
                <label className="block text-sm font-medium text-[var(--v-text-primary)] mb-2">Título</label>
                <input
                  type="text"
                  value={newEvent.title}
                  onChange={(e) => setNewEvent({ ...newEvent, title: e.target.value })}
                  className="w-full px-4 py-2.5 border border-[var(--v-border)] rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  placeholder="Nombre del evento"
                  required
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-[var(--v-text-primary)] mb-2">Tipo</label>
                <select
                  value={newEvent.type}
                  onChange={(e) => setNewEvent({ ...newEvent, type: e.target.value as CalendarEvent['type'] })}
                  className="w-full px-4 py-2.5 border border-[var(--v-border)] rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                >
                  {Object.entries(eventTypes).map(([key, type]) => (
                    <option key={key} value={key}>{type.label}</option>
                  ))}
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-[var(--v-text-primary)] mb-2">Ubicación</label>
                <input
                  type="text"
                  value={newEvent.location}
                  onChange={(e) => setNewEvent({ ...newEvent, location: e.target.value })}
                  className="w-full px-4 py-2.5 border border-[var(--v-border)] rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  placeholder="Ej: Salón de Eventos"
                />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-[var(--v-text-primary)] mb-2">Inicio</label>
                  <input
                    type="datetime-local"
                    value={newEvent.startDate}
                    onChange={(e) => setNewEvent({ ...newEvent, startDate: e.target.value })}
                    className="w-full px-4 py-2.5 border border-[var(--v-border)] rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    required
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-[var(--v-text-primary)] mb-2">Fin (opcional)</label>
                  <input
                    type="datetime-local"
                    value={newEvent.endDate}
                    onChange={(e) => setNewEvent({ ...newEvent, endDate: e.target.value })}
                    className="w-full px-4 py-2.5 border border-[var(--v-border)] rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  />
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium text-[var(--v-text-primary)] mb-2">Descripción</label>
                <textarea
                  value={newEvent.description}
                  onChange={(e) => setNewEvent({ ...newEvent, description: e.target.value })}
                  rows={3}
                  className="w-full px-4 py-2.5 border border-[var(--v-border)] rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent resize-none"
                  placeholder="Detalles del evento..."
                />
              </div>
              <div className="flex gap-3 pt-4">
                <button
                  type="button"
                  onClick={() => setShowModal(false)}
                  className="flex-1 px-4 py-2.5 border border-[var(--v-border)] text-[var(--v-text-primary)] rounded-lg font-medium hover:bg-[var(--v-surface-2)] transition-colors"
                >
                  Cancelar
                </button>
                <button
                  type="submit"
                  className="flex-1 px-4 py-2.5 bg-blue-600 hover:bg-blue-700 text-white rounded-lg font-medium transition-colors"
                >
                  Crear Evento
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Event Detail Modal */}
      {selectedEvent && (
        <div className="fixed inset-0 bg-slate-900/50 flex items-center justify-center z-50 p-4">
          <div className="bg-[var(--v-surface)] rounded-2xl shadow-xl max-w-md w-full">
            <div className="p-6 border-b border-[var(--v-border)] flex items-start justify-between">
              <div className="flex items-start gap-3">
                <div className={`w-12 h-12 ${eventTypes[selectedEvent.type].color} rounded-xl flex items-center justify-center`}>
                  <CalendarIcon className="w-6 h-6 text-white" />
                </div>
                <div>
                  <h3 className="text-lg font-semibold text-[var(--v-text-primary)]">{selectedEvent.title}</h3>
                  <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${eventTypes[selectedEvent.type].bg} ${eventTypes[selectedEvent.type].text} border ${eventTypes[selectedEvent.type].border}`}>
                    {eventTypes[selectedEvent.type].label}
                  </span>
                </div>
              </div>
              <button 
                onClick={() => setSelectedEvent(null)}
                className="p-2 text-[var(--v-text-tertiary)] hover:text-[var(--v-text-secondary)] hover:bg-[var(--v-surface-2)] rounded-lg transition-colors"
              >
                <X className="w-5 h-5" />
              </button>
            </div>
            <div className="p-6 space-y-4">
              <p className="text-[var(--v-text-secondary)]">{selectedEvent.description}</p>

              <div className="rounded-xl border border-[var(--v-border)] bg-[var(--v-surface-2)] p-4">
                <div className="flex items-center justify-between mb-3">
                  <h4 className="text-sm font-semibold text-[var(--v-text-primary)]">¿Asistirás?</h4>
                  <span className="text-xs text-[var(--v-text-secondary)]">{rsvpSummary?.total ?? 0} respuestas</span>
                </div>
                {rsvpError && <p className="mb-2 text-xs text-red-600">{rsvpError}</p>}
                <div className="grid grid-cols-3 gap-2">
                  {([['attending', 'Asistiré'], ['not_attending', 'No asistiré'], ['maybe', 'Tal vez']] as const).map(([status, label]) => (
                    <button key={status} type="button" disabled={savingRsvp} onClick={() => submitRsvp(status)}
                      className={`px-2 py-2 rounded-lg text-xs font-medium border transition-colors ${myRsvp === status ? 'bg-blue-600 text-white border-blue-600' : 'bg-[var(--v-surface)] text-[var(--v-text-secondary)] border-[var(--v-border)] hover:border-blue-400'}`}>
                      {label}{rsvpSummary && <span className="block text-[10px] opacity-75">{rsvpSummary[status]}</span>}
                    </button>
                  ))}
                </div>
              </div>
              
              <div className="space-y-3">
                <div className="flex items-center gap-3 text-sm">
                  <Clock className="w-4 h-4 text-[var(--v-text-tertiary)]" />
                  <span className="text-[var(--v-text-secondary)]">
                    {new Date(selectedEvent.startDate).toLocaleDateString('es-ES', { weekday: 'long', day: 'numeric', month: 'long' })}
                    {' • '}
                    {new Date(selectedEvent.startDate).toLocaleTimeString('es-ES', { hour: '2-digit', minute: '2-digit' })}
                    {selectedEvent.endDate && ` - ${new Date(selectedEvent.endDate).toLocaleTimeString('es-ES', { hour: '2-digit', minute: '2-digit' })}`}
                  </span>
                </div>
                {selectedEvent.location && (
                  <div className="flex items-center gap-3 text-sm">
                    <MapPin className="w-4 h-4 text-[var(--v-text-tertiary)]" />
                    <span className="text-[var(--v-text-secondary)]">{selectedEvent.location}</span>
                  </div>
                )}
              </div>
            </div>
            <div className="p-6 border-t border-[var(--v-border)] bg-[var(--v-surface-2)] flex justify-end gap-3">
              {isAdminRole && (
                <button
                  onClick={() => openEditModal(selectedEvent)}
                  className="px-4 py-2.5 text-blue-600 hover:bg-blue-50 rounded-lg font-medium transition-colors flex items-center gap-2"
                >
                  <Edit2 className="w-4 h-4" />
                  Editar
                </button>
              )}
              <button 
                onClick={() => deleteEvent(selectedEvent.id)}
                className="px-4 py-2.5 text-red-600 hover:bg-red-50 rounded-lg font-medium transition-colors"
              >
                Eliminar
              </button>
              <button 
                onClick={() => setSelectedEvent(null)}
                className="px-4 py-2.5 bg-blue-600 hover:bg-blue-700 text-white rounded-lg font-medium transition-colors"
              >
                Cerrar
              </button>
            </div>
          </div>
        </div>
      )}
      {/* F51: Edit Event Modal */}
      {showEditModal && (
        <div className="fixed inset-0 bg-slate-900/50 flex items-center justify-center z-50 p-4">
          <div className="bg-[var(--v-surface)] rounded-2xl shadow-xl max-w-md w-full max-h-[90vh] overflow-y-auto">
            <div className="p-6 border-b border-[var(--v-border)] flex items-center justify-between">
              <h3 className="text-lg font-semibold text-[var(--v-text-primary)]">Editar Evento</h3>
              <button
                onClick={() => setShowEditModal(false)}
                className="p-2 text-[var(--v-text-tertiary)] hover:text-[var(--v-text-secondary)] hover:bg-[var(--v-surface-2)] rounded-lg transition-colors"
              >
                <X className="w-5 h-5" />
              </button>
            </div>
            <form onSubmit={handleEditEvent} className="p-6 space-y-4">
              {editError && (
                <div className="bg-red-50 border border-red-200 text-red-600 text-sm rounded-lg px-4 py-3">
                  {editError}
                </div>
              )}
              <div>
                <label className="block text-sm font-medium text-[var(--v-text-primary)] mb-2">Título</label>
                <input
                  type="text"
                  value={editEvent.title}
                  onChange={(e) => setEditEvent({ ...editEvent, title: e.target.value })}
                  className="w-full px-4 py-2.5 border border-[var(--v-border)] rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  placeholder="Nombre del evento"
                  required
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-[var(--v-text-primary)] mb-2">Tipo</label>
                <select
                  value={editEvent.type}
                  onChange={(e) => setEditEvent({ ...editEvent, type: e.target.value })}
                  className="w-full px-4 py-2.5 border border-[var(--v-border)] rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                >
                  {Object.entries(eventTypes).map(([key, type]) => (
                    <option key={key} value={key}>{type.label}</option>
                  ))}
                </select>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-[var(--v-text-primary)] mb-2">Inicio</label>
                  <input
                    type="datetime-local"
                    value={editEvent.date}
                    onChange={(e) => setEditEvent({ ...editEvent, date: e.target.value })}
                    className="w-full px-4 py-2.5 border border-[var(--v-border)] rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    required
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-[var(--v-text-primary)] mb-2">Fin (opcional)</label>
                  <input
                    type="datetime-local"
                    value={editEvent.endDate}
                    onChange={(e) => setEditEvent({ ...editEvent, endDate: e.target.value })}
                    className="w-full px-4 py-2.5 border border-[var(--v-border)] rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  />
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium text-[var(--v-text-primary)] mb-2">Ubicación</label>
                <input
                  type="text"
                  value={editEvent.location}
                  onChange={(e) => setEditEvent({ ...editEvent, location: e.target.value })}
                  className="w-full px-4 py-2.5 border border-[var(--v-border)] rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  placeholder="Ej: Salón de Eventos"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-[var(--v-text-primary)] mb-2">Descripción</label>
                <textarea
                  value={editEvent.description}
                  onChange={(e) => setEditEvent({ ...editEvent, description: e.target.value })}
                  rows={3}
                  className="w-full px-4 py-2.5 border border-[var(--v-border)] rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent resize-none"
                  placeholder="Detalles del evento..."
                />
              </div>
              <div className="flex gap-3 pt-4">
                <button
                  type="button"
                  onClick={() => setShowEditModal(false)}
                  className="flex-1 px-4 py-2.5 border border-[var(--v-border)] text-[var(--v-text-primary)] rounded-lg font-medium hover:bg-[var(--v-surface-2)] transition-colors"
                >
                  Cancelar
                </button>
                <button
                  type="submit"
                  disabled={savingEdit}
                  className="flex-1 px-4 py-2.5 bg-blue-600 hover:bg-blue-700 disabled:bg-blue-400 text-white rounded-lg font-medium transition-colors flex items-center justify-center gap-2"
                >
                  {savingEdit ? (
                    <><Loader2 className="w-4 h-4 animate-spin" /> Guardando...</>
                  ) : (
                    'Guardar Cambios'
                  )}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
